


import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ApiCallService from "../../../Service/ApiCallService";

const emptyText = "-";

const dateFields = ["startDate", "endDate", "createdAt", "updatedAt", "deletedAt"];

const getUserName = (user) => {
  if (!user) return emptyText;
  const name = [user.userFirstName, user.userLastName].filter(Boolean).join(" ");
  return name || user.name || user.mobileNumber || user._id || emptyText;
};

const getInitials = (user) => {
  const name = getUserName(user);
  if (!name || name === emptyText) return "?";

  return name
    .split(" ")
    .slice(0, 2)
    .map((item) => item[0])
    .join("")
    .toUpperCase();
};

const formatDateOnly = (value) => {
  if (!value) return emptyText;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return emptyText;

  return date.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return emptyText;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return emptyText;

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatEventDate = (value, meetingType) => {
  if (!value) return emptyText;

  if (meetingType === "Offline") {
    return formatDateOnly(value);
  }

  return formatDateTime(value);
};

const toLabel = (key) =>
  String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const getReadableName = (value) => {
  if (!value) return emptyText;

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.length ? value.map(getReadableName).join(", ") : emptyText;
  }

  if (typeof value === "object") {
    if (value.name) return value.name;
    if (value.heading) return value.heading;
    if (value.title) return value.title;
    if (value.chapterName) return value.chapterName;
    if (value.userFirstName || value.userLastName) return getUserName(value);
    if (value.programId?.name) return value.programId.name;
    if (value.calendarTypeId?.name) return value.calendarTypeId.name;
    if (value.calendarSubTypeId?.name) return value.calendarSubTypeId.name;

    return value._id || emptyText;
  }

  return String(value);
};

const formatValue = (value, key, meetingType) => {
  if (value === null || value === undefined || value === "") return emptyText;

  if (dateFields.includes(key)) {
    return formatEventDate(value, meetingType);
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  return getReadableName(value);
};

const getActionMeta = (action) => {
  if (action === "CREATED") {
    return {
      title: "Event created",
      icon: <AddCircleOutlineIcon />,
      color: "success",
    };
  }

  if (action === "DELETED") {
    return {
      title: "Event deleted",
      icon: <DeleteOutlineIcon />,
      color: "error",
    };
  }

  if (action === "UPDATED") {
    return {
      title: "Event updated",
      icon: <EditNoteIcon />,
      color: "primary",
    };
  }

  return {
    title: "History updated",
    icon: <HistoryIcon />,
    color: "default",
  };
};

const CompactValueBox = ({ label, oldValue, newValue }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 2,
      bgcolor: "grey.50",
      height: "100%",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
      {label}
    </Typography>

    <Grid container spacing={1} sx={{ mt: 0.5 }}>
      <Grid item xs={12} md={6}>
        <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: "#fff5f5", minHeight: 54 }}>
          <Typography variant="caption" color="text.secondary">
            Old
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
            {oldValue}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: "#f0fff4", minHeight: 54 }}>
          <Typography variant="caption" color="text.secondary">
            New
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
            {newValue}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Paper>
);

const CalendarEventHistoryPage = () => {
  const { eventId, id } = useParams();
  const navigate = useNavigate();
  const resolvedEventId = eventId || id;

  const [event, setEvent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const fetchEventHistory = async () => {
      if (!resolvedEventId) {
        setError("Event id is missing.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/calendarbooking/${resolvedEventId}`
        );

        if (!active) return;

        if (response?.success === false) {
          throw new Error(response?.message || "Unable to load event history.");
        }

        const eventData = response?.data || null;
        setEvent(eventData);
        setHistory(Array.isArray(eventData?.history) ? eventData.history : []);
      } catch (err) {
        if (!active) return;

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load event history."
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchEventHistory();

    return () => {
      active = false;
    };
  }, [resolvedEventId]);

  const sortedHistory = useMemo(() => {
    return [...history].sort(
      (a, b) =>
        new Date(b?.updatedAt || 0).getTime() -
        new Date(a?.updatedAt || 0).getTime()
    );
  }, [history]);

  const meetingType = event?.meetingType || "Online";

  const renderChanges = (item) => {
    const changes = item?.changes || {};
    const fields = Object.keys(changes);

    if (!fields.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No field changes available.
        </Typography>
      );
    }

    return (
      <Grid container spacing={1.25}>
        {fields.map((key) => {
          const change = changes[key];

          return (
            <Grid item xs={12} md={6} key={key}>
              <CompactValueBox
                label={toLabel(key)}
                oldValue={formatValue(change?.previous, key, meetingType)}
                newValue={formatValue(change?.new, key, meetingType)}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderHistoryItem = (item, index) => {
    const meta = getActionMeta(item?.action);

    return (
      <Card
        key={`${item?.updatedAt || index}-${item?.action || "history"}`}
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          borderLeft: "5px solid",
          borderLeftColor: `${meta.color}.main`,
          boxShadow: "0 4px 14px rgba(15,23,42,0.05)",
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar sx={{ width: 34, height: 34, bgcolor: `${meta.color}.main` }}>
                {meta.icon}
              </Avatar>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  {meta.title}
                </Typography>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={item?.action || "Change"} color={meta.color} />
                  <Chip
                    size="small"
                    label={`By ${getUserName(item?.updatedBy)}`}
                    variant="outlined"
                  />
                </Stack>
              </Box>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              {formatDateTime(item?.updatedAt)}
            </Typography>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {renderChanges(item)}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading event history...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        bgcolor: "grey.50",
        minHeight: "100vh",
      }}
    >
      <Stack spacing={1.75}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.25}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Calendar Event History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Event creation, update and delete history.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Paper
          variant="outlined"
          sx={{
            p: 1.75,
            borderRadius: 2.5,
            bgcolor: "background.paper",
          }}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                EVENT
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {event?.heading || emptyText}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {event?.subheading || emptyText}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={getReadableName(event?.calendarTypeId)} />
                <Chip size="small" label={getReadableName(event?.calendarSubTypeId)} variant="outlined" />
                <Chip size="small" label={event?.meetingType || emptyText} color="primary" />
                <Chip
                  size="small"
                  label={`Start: ${formatEventDate(event?.startDate, meetingType)}`}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`End: ${formatEventDate(event?.endDate, meetingType)}`}
                  variant="outlined"
                />
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Created by {getUserName(event?.createdBy)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Stack direction="row" alignItems="center" spacing={1}>
          <HistoryIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
            Activity Timeline
          </Typography>
          <Chip size="small" label={`${sortedHistory.length} events`} />
        </Stack>

        {!sortedHistory.length ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: "center",
              borderRadius: 2.5,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              No history found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This event does not have any changes yet.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>{sortedHistory.map(renderHistoryItem)}</Stack>
        )}
      </Stack>
    </Box>
  );
};

export default CalendarEventHistoryPage;